import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  formatQueryParams,
  JsonApiResponseDto,
  parseJsonApiResponse,
  prepareHeaders,
  Response,
  TQueryParams,
} from 'evolve-ui';
import { TAgency } from '../..';
import { TUser } from './users.api';

export type TCopyAgencyCustomerUserLinksBody = {
  userToAdd: number;
  userToCopy: number;
  agencyId?: number;
  customerIds?: number[];
  contactIds?: number[];
};

export type TTransferCustomerUserFieldBody = {
  originUserId: number;
  destinationUserId: number;
  dryRun?: boolean;
  customerIds?: number[];
};

export type TPreviewAgencyCustomerUserLinkRow = {
  type: string;
  id: string;
  agencyId: number;
  userPrimaryCustomer?: number | null;
  customerId: number;
  name?: string | null;
  number?: string | null;
  status?: number | null;
  fiscalNumber?: string | null;
  contactsCount: number;
  userToAdd: number;
  userToCopy: number;
  scopedAgencyId?: number | null;
  customerLinkCount: number;
  contactLinkCount: number;
};

export type TPreviewAgencyCustomerUserLinkContactRow = {
  type: string;
  id: string;
  customerId: number;
  customerName?: string | null;
  contactId: number;
  name?: string | null;
  phone?: string | null;
  cellphone?: string | null;
  email?: string | null;
  userToAdd: number;
  userToCopy: number;
  scopedAgencyId?: number | null;
  customerLinkCount: number;
  contactLinkCount: number;
};

export type TTransferCustomerUserPreviewRow = {
  type: string;
  id: string;
  originUserId: number;
  destinationUserId: number;
  matched: number;
  mongoId?: string;
  portalId?: number | null;
  name?: string | null;
  fiscalNumber?: string | null;
  number?: string | null;
  status?: number | null;
  responsibleCharges?: unknown;
  manager?: unknown;
  contactsCount?: number;
};

/** @deprecated Use paginated row types instead */
export type TPreviewAgencyCustomerUserLinksResponse = {
  type: string;
  id: string;
  userToAdd: number;
  userToCopy: number;
  scopedAgencyId?: number | null;
  excludeCustomerIds?: number[];
  excludeContactIds?: number[];
  customerLinkCount: number;
  contactLinkCount: number;
  customerLinks: {
    agencyId: number;
    userPrimaryCustomer?: boolean;
    customer: {
      id: number;
      name?: string;
      number?: string;
      customerStatusId: number;
      fiscalNumber?: string;
    };
    contacts: {
      contactId: number;
      contactName?: string;
      email?: string;
      phone?: string;
      cellphone?: string;
    }[];
  }[];
};

export type TCopyAgencyCustomerUserLinksResponse = {
  type: string;
  id: string;
  inserted: number;
  contactsInserted: number;
  scopedAgencyId?: number | null;
  customerIds?: number[];
  contactIds?: number[];
  runId: string;
};

/** @deprecated Use paginated row types instead */
export type TTransferCustomerUserPreviewResponse = {
  type: string;
  id: string;
  originUserId: number;
  destinationUserId: number;
  matched: number;
  customers: {
    mongoId?: string;
    portalId?: number;
    name?: string;
    fiscalNumber?: string;
    responsibleCharges?: number;
    manager?: number;
    number?: string;
    contactsCount?: number;
    status?: number;
  }[];
};

export type TTransferCustomerUserResponse = {
  type: string;
  id: string;
  matched: number;
  updated: number;
  failed: number;
  errors: unknown[];
  runId?: string | null;
};

export enum CUSTOMER_MIGRATION_RUN_TYPES {
  CUSTOMER_USER_LINK_COPY_RUN = 'customer-user-link-copy-run',
  MANAGER_TRANSFER_RUN = 'manager-transfer-run',
  RESPONSIBLE_CHARGES_TRANSFER_RUN = 'responsible-charges-transfer-run',
}

export enum CUSTOMER_MIGRATION_RUN_STATUS {
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

type CustomerInfo = {
  agency?: {
    id: number;
    name: string;
    acronym: string;
  };
  customer: {
    portalId: number;
    mongoId: string;
    name: string;
    number: string;
    fiscalNumber: string;
  };
  userPrimaryCustomer?: number;
};

type ContactInfo = {
  agency: {
    id: number;
    name: string;
    acronym: string;
  };
  customer?: {
    portalId: number;
    mongoId: string;
    name: string;
    number: string;
    fiscalNumber: string;
  };
  contact: {
    id: number;
    name: string;
  };
  userPrimaryContact: number;
};

export type TCustomerMigrationHistory = {
  migrationType: CUSTOMER_MIGRATION_RUN_TYPES;
  transferStatus: CUSTOMER_MIGRATION_RUN_STATUS;
  id: string;
  runId: string;
  createdAt?: string;
  originUser: TUser;
  destinationUser: TUser;
  agency?: TAgency;
  actorUserId: number;
  scopedAgencyId: number;
  reverted: boolean;
  revertedAt?: string;
  revertInProgress?: boolean;
  revertStartedAt?: string;
  customersMigrations: {
    excluded?: CustomerInfo[];
    transferred: CustomerInfo[];
  };
  contactsMigrations: {
    //original?: ContactInfo[];
    excluded?: ContactInfo[];
    transferred: ContactInfo[];
  };
};

function withMigrationRevertedFlag<T extends TCustomerMigrationHistory>(
  row: T,
): T {
  return {
    ...row,
    reverted: row.reverted === true || row.revertedAt != null,
  };
}

function normalizeMigrationHistoryResponse<T extends TCustomerMigrationHistory>(
  response: Response<T>,
): Response<T> {
  return {
    ...response,
    values: response.values.map(withMigrationRevertedFlag),
  };
}

export type TRevertMigrationHistoryRunResponse = {
  type: string;
  id: string;
  runId: string;
  migrationType:
    | 'manager-transfer-run'
    | 'responsible-charges-transfer-run'
    | 'customer-user-link-copy-run'
    | null;
  status:
    | 'not_found'
    | 'already_reverted'
    | 'reverted'
    | 'revert_in_progress'
    | 'revert_incomplete'
    | 'revert_blocked';
  revertedAt?: string;
  revertStartedAt?: string;
  metrics?: Record<string, number>;
  errors?: string[];
};

export const usersMigrationsApi = createApi({
  reducerPath: 'usersMigrations',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_CUSTOMERS_API_URL}/customers/tools`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['UsersMigration'],
  endpoints: (builder) => ({
    previewAgencyCustomerUserLinks: builder.query<
      Response<TPreviewAgencyCustomerUserLinkRow>,
      TQueryParams
    >({
      query: (params) =>
        formatQueryParams(params, 'preview-agency-customer-user-links'),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TPreviewAgencyCustomerUserLinkRow>(
          res as JsonApiResponseDto<TPreviewAgencyCustomerUserLinkRow>,
          meta,
          args,
          true,
        ),
    }),
    previewAgencyCustomerUserLinksContacts: builder.query<
      Response<TPreviewAgencyCustomerUserLinkContactRow>,
      TQueryParams
    >({
      query: (params) =>
        formatQueryParams(
          params,
          'preview-agency-customer-user-links/contacts',
        ),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TPreviewAgencyCustomerUserLinkContactRow>(
          res as JsonApiResponseDto<TPreviewAgencyCustomerUserLinkContactRow>,
          meta,
          args,
          true,
        ),
    }),
    copyAgencyCustomerUserLinks: builder.mutation<
      Response<TCopyAgencyCustomerUserLinksResponse>,
      TCopyAgencyCustomerUserLinksBody
    >({
      invalidatesTags: ['UsersMigration'],
      query: (body) => ({
        url: 'copy-agency-customer-user-links',
        method: 'POST',
        body,
      }),
      transformResponse:
        parseJsonApiResponse<TCopyAgencyCustomerUserLinksResponse>,
    }),
    previewTransferResponsibleCharges: builder.query<
      Response<TTransferCustomerUserPreviewRow>,
      TQueryParams
    >({
      query: (params) =>
        formatQueryParams(params, 'preview-transfer-responsible-charges'),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TTransferCustomerUserPreviewRow>(
          res as JsonApiResponseDto<TTransferCustomerUserPreviewRow>,
          meta,
          args,
          true,
        ),
    }),
    transferResponsibleCharges: builder.mutation<
      Response<TTransferCustomerUserResponse>,
      TTransferCustomerUserFieldBody
    >({
      invalidatesTags: ['UsersMigration'],
      query: (body) => ({
        url: 'transfer-responsible-charges',
        method: 'POST',
        body,
      }),
      transformResponse: parseJsonApiResponse<TTransferCustomerUserResponse>,
    }),
    previewTransferManager: builder.query<
      Response<TTransferCustomerUserPreviewRow>,
      TQueryParams
    >({
      query: (params) =>
        formatQueryParams(params, 'preview-transfer-manager'),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TTransferCustomerUserPreviewRow>(
          res as JsonApiResponseDto<TTransferCustomerUserPreviewRow>,
          meta,
          args,
          true,
        ),
    }),
    transferManager: builder.mutation<
      Response<TTransferCustomerUserResponse>,
      TTransferCustomerUserFieldBody
    >({
      invalidatesTags: ['UsersMigration'],
      query: (body) => ({
        url: 'transfer-manager',
        method: 'POST',
        body,
      }),
      transformResponse: parseJsonApiResponse<TTransferCustomerUserResponse>,
    }),
    getMigrationHistory: builder.query<
      Response<TCustomerMigrationHistory>,
      TQueryParams
    >({
      providesTags: ['UsersMigration'],
      query: (params) => ({
        url: formatQueryParams(params, 'migration-history'),
      }),
      transformResponse: (res, meta, args) =>
        normalizeMigrationHistoryResponse(
          parseJsonApiResponse<TCustomerMigrationHistory>(
            res as JsonApiResponseDto<TCustomerMigrationHistory>,
            meta,
            args,
            false,
          ),
        ),
    }),
    getMigrationHistoryDetail: builder.query<
      Response<TCustomerMigrationHistory>,
      { id: string }
    >({
      providesTags: ['UsersMigration'],
      query: ({ id }) => ({
        url: `migration-history/${encodeURIComponent(id)}`,
      }),
      transformResponse: (res) =>
        normalizeMigrationHistoryResponse(
          parseJsonApiResponse<TCustomerMigrationHistory>(res),
        ),
    }),
    revertMigrationHistoryRun: builder.mutation<
      Response<TRevertMigrationHistoryRunResponse>,
      { runId: string }
    >({
      invalidatesTags: ['UsersMigration'],
      query: (body) => ({
        url: 'migration-history/revert',
        method: 'POST',
        body,
      }),
      transformResponse:
        parseJsonApiResponse<TRevertMigrationHistoryRunResponse>,
    }),
  }),
});

export const {
  usePreviewAgencyCustomerUserLinksQuery,
  useLazyPreviewAgencyCustomerUserLinksQuery,
  usePreviewAgencyCustomerUserLinksContactsQuery,
  useLazyPreviewAgencyCustomerUserLinksContactsQuery,
  useCopyAgencyCustomerUserLinksMutation,
  usePreviewTransferResponsibleChargesQuery,
  useLazyPreviewTransferResponsibleChargesQuery,
  useTransferResponsibleChargesMutation,
  usePreviewTransferManagerQuery,
  useLazyPreviewTransferManagerQuery,
  useTransferManagerMutation,
  useGetMigrationHistoryQuery,
  useGetMigrationHistoryDetailQuery,
  useRevertMigrationHistoryRunMutation,
} = usersMigrationsApi;
